import { EditOutlined, RobotOutlined } from '@ant-design/icons';

import { Badge, Button, Skeleton } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { soVN } from '../../../lib/dinh-dang';
import type { BanDanhGia } from '../types';

export interface BanDanhGiaAiProps {
  ban: BanDanhGia | undefined;
  dangTai: boolean;
  loi: unknown;
  /** Người dùng có quyền `tro-ly:viet-lai`; thiếu thì ẩn nút (ADR-UI-0005). */
  duocVietLai: boolean;
  dangVietLai: boolean;
  onVietLai: () => void;
  /** Tên tiêu chí theo mã, để danh sách bỏ sót đọc được thay vì chỉ hiện mã. */
  tenTheoMa: Record<string, string>;
}

/**
 * Bản đánh giá tổng thể của trợ lí AI (đặc tả mục 10.4, Mốc 10).
 *
 * Quyết định trình bày quan trọng nhất: **danh sách bỏ sót nằm TRƯỚC bản đánh giá**, không nằm dưới.
 *
 * Đặc tả mục 8.3 nói rõ vì sao — lỗi bỏ sót nguy hiểm hơn lỗi nói sai: bản đánh giá đọc trôi chảy, mạch lạc,
 * và thiếu đúng cái tiêu chí loại trừ đang không đạt. Đặt cảnh báo dưới một bản văn dài là để nó không được
 * đọc; đặt trên là buộc người đọc thấy nó trước khi tin bản đánh giá.
 *
 * Nội dung hiện dạng **văn bản thuần giữ dòng**, không render markdown: kho chưa có thư viện markdown, và
 * thêm một thư viện để in `##` đậm hơn là đổi bề mặt phụ thuộc cho một lợi ích nhỏ. Bản đánh giá có tiêu đề
 * `##` và gạch đầu dòng nên đọc được nguyên văn.
 */
export function BanDanhGiaAi({
  ban, dangTai, loi, duocVietLai, dangVietLai, onVietLai, tenTheoMa,
}: BanDanhGiaAiProps) {
  const { t, locale } = useT();
  const dichLoi = useLoi();

  if (dangTai) {
    return (
      <div className="space-y-2" aria-busy="true">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (loi) {
    return (
      <p className="rounded border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
        {dichLoi(loi)}
      </p>
    );
  }

  const dangChay = ban ? ['cho', 'dang_chay'].includes(ban.trang_thai) : false;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <RobotOutlined className="shrink-0 text-primary-600" />
        <p className="min-w-0 flex-1 text-sm text-content-muted">
          {ban?.trang_thai === 'xong' && ban.mo_hinh
            ? t('thamDinh.raSoat.danhGiaBoi', { model: ban.mo_hinh, lan: soVN(ban.lan, locale) })
            : t('thamDinh.raSoat.danhGiaMo')}
        </p>
        {duocVietLai && !dangChay && (
          <Button
            variant="outline"
            size="sm"
            icon={<EditOutlined />}
            loading={dangVietLai}
            onClick={onVietLai}
          >
            {t(ban?.trang_thai === 'xong' ? 'thamDinh.raSoat.danhGiaVietLai' : 'thamDinh.raSoat.danhGiaViet')}
          </Button>
        )}
      </div>

      {(!ban || ban.trang_thai === 'chua_co') && (
        <p className="text-sm text-content-muted">{t('thamDinh.raSoat.danhGiaChuaCo')}</p>
      )}

      {dangChay && (
        <div className="flex items-center gap-2 rounded border border-primary-200 bg-primary-50 p-3">
          <span className="size-2 animate-pulse rounded-full bg-primary-500" aria-hidden="true" />
          <p className="text-sm text-primary-800">{t('thamDinh.raSoat.danhGiaDangViet')}</p>
        </div>
      )}

      {ban?.trang_thai === 'loi' && (
        <div className="rounded border border-danger-200 bg-danger-50 p-3">
          <p className="font-medium text-danger-800">{t('thamDinh.raSoat.danhGiaLoi')}</p>
          <p className="mt-1 text-sm text-danger-700">{ban.loi}</p>
        </div>
      )}

      {ban?.trang_thai === 'xong' && (
        <>
          {/*
            * TRƯỚC bản đánh giá, không sau: đặc tả mục 8.3 — bản đánh giá đọc trôi chảy và thiếu đúng cái
            * tiêu chí đang không đạt, nên cảnh báo phải chắn đường người đọc.
            */}
          {ban.nguon_chua_dan.length > 0 && (
            <div className="rounded border border-warning-200 bg-warning-50 p-3">
              <p className="font-medium text-warning-800">
                {t('thamDinh.raSoat.danhGiaBoSot', { so: soVN(ban.nguon_chua_dan.length, locale) })}
              </p>
              <ul className="mt-1 space-y-0.5">
                {ban.nguon_chua_dan.map((ma) => (
                  <li key={ma} className="text-sm text-warning-800">
                    {tenTheoMa[ma] ?? ma}
                    {tenTheoMa[ma] && <code className="ms-1 text-2xs text-content-muted">{ma}</code>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {ban.noi_dung.trim() ? (
            <div className="whitespace-pre-wrap rounded border border-default bg-surface p-3 text-sm">
              {ban.noi_dung}
            </div>
          ) : (
            /*
             * Đã chạy mà ra rỗng: KHÁC `chua_co`. Nói đúng điều đó thay vì mời bấm lại một nút vừa chạy —
             * người dùng cần biết mô hình đã trả lời và câu trả lời là rỗng.
             */
            <div className="rounded border border-warning-200 bg-warning-50 p-3">
              <Badge variant="warning">{t('thamDinh.raSoat.danhGiaRong')}</Badge>
              <p className="mt-1 text-sm text-warning-800">{t('thamDinh.raSoat.danhGiaRongMo')}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
